'use client';

import { ChevronDown, FlagEN, FlagFR } from '@/components/ui/icons/Icons';
import { AVAILABLE_LANGUAGES, LanguageCode } from '@/locales';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { useI18n } from '@/context/I18nProvider';
import { cn } from '@/lib/utils';
import { useState } from 'react';

type ChevronPosition = 'left' | 'right' | 'responsive';

interface LanguageFlagDropdownProps {
  /** Where to place the chevron relative to the flag:
   * - 'left': chevron on the left (flag on right edge of screen)
   * - 'right': chevron on the right (flag on left edge of screen)
   * - 'responsive': left on mobile (right edge), right on lg+ (left edge)
   */
  chevronPosition?: ChevronPosition;
  dropdownAlign?: 'start' | 'end';
  buttonClassName?: string;
}

const LanguageFlagDropdown = ({
  chevronPosition = 'left',
  dropdownAlign = 'end',
  buttonClassName,
}: LanguageFlagDropdownProps) => {
  const { locale, setLocale, t } = useI18n();
  const [open, setOpen] = useState(false);

  const chevronElement = (
    <ChevronDown
      className={cn(
        'w-3.5 h-3.5 text-gray-400 transition-transform duration-200',
        open && 'rotate-180'
      )}
    />
  );

  const flagElement =
    locale === 'fr' ? (
      <FlagFR className="w-7 h-[21px] rounded-sm" />
    ) : (
      <FlagEN className="w-7 h-[21px] rounded-sm" />
    );

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex items-center justify-center gap-1 p-1.5 focus:outline-none',
            buttonClassName
          )}
          aria-label={t('ui.selectLanguage')}
        >
          {chevronPosition === 'left' && (
            <>
              {chevronElement}
              {flagElement}
            </>
          )}
          {chevronPosition === 'right' && (
            <>
              {flagElement}
              {chevronElement}
            </>
          )}
          {chevronPosition === 'responsive' && (
            <>
              {/* Mobile: flag on right edge → chevron on left */}
              <span className="contents lg:hidden">
                {chevronElement}
                {flagElement}
              </span>
              {/* Desktop: flag on left edge → chevron on right */}
              <span className="hidden lg:contents">
                {flagElement}
                {chevronElement}
              </span>
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={dropdownAlign} className="min-w-[160px] rounded-xl shadow-md p-1">
        {AVAILABLE_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            className={cn(
              'cursor-pointer px-3 py-2 text-sm gap-2.5 rounded-lg',
              locale === lang.code
                ? 'font-semibold bg-blue-50 text-blue-600 focus:!bg-blue-50 focus:!text-blue-600'
                : 'focus:bg-gray-50'
            )}
            onClick={() => setLocale(lang.code as LanguageCode)}
          >
            {lang.code === 'fr' ? (
              <div className="w-5 h-[14px] rounded-sm overflow-hidden shrink-0">
                <FlagFR className="w-full h-full" />
              </div>
            ) : (
              <div className="w-5 h-[14px] rounded-sm overflow-hidden shrink-0">
                <FlagEN className="w-full h-full" />
              </div>
            )}
            {lang.nativeName}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { LanguageFlagDropdown };
